// frontend/src/api/workOptimization.ts
import api from './client';

export type OperationCategory = 'productive' | 'walking' | 'waiting' | 'preparation' | 'loss';

export interface WorkType { id: number; name: string; }
export interface Operation {
  id: number; name: string; category: OperationCategory;
  work_type_id: number | null; work_type?: WorkType;
}
export interface IndividualRef { id: number; first_name: string; last_name: string; }
export interface WorkplaceRef { id: number; name: string; }
export interface UserRef { id: number; username: string; display_name?: string; }

export interface TimingEvent {
  id: number; session_id: number;
  operation_id: number | null; custom_name: string | null;
  category: OperationCategory; timestamp: string;
  operation?: Operation;
}
export interface TimingSession {
  id: number; work_type: WorkType; workplace?: WorkplaceRef;
  individual?: IndividualRef; observer: UserRef;
  car_info?: string; notes?: string;
  started_at: string; ended_at?: string;
  events: TimingEvent[];
}
export interface TimingSessionListItem {
  id: number; work_type: WorkType; workplace?: WorkplaceRef;
  individual?: IndividualRef; car_info?: string;
  started_at: string; ended_at?: string; event_count: number;
}

export interface CategoryStat {
  category: string; label: string;
  duration_seconds: number; percent: number; color: string;
}
export interface OperationStat {
  name: string; category: string; duration_seconds: number; percent: number;
}
export interface TimelineSegment {
  name: string; category: string; color: string;
  start_seconds: number; duration_seconds: number;
}
export interface SessionAnalysis {
  session_id: number; total_seconds: number;
  category_stats: CategoryStat[];
  operation_stats: OperationStat[];
  timeline: TimelineSegment[];
}

export interface ProcedureStep {
  order: number; operation_id?: number; name: string;
  norm_seconds: number; category: OperationCategory;
}
export interface OptimalProcedure {
  id: number; work_type: WorkType; workplace?: WorkplaceRef;
  name: string; creator: UserRef; steps: ProcedureStep[]; created_at: string;
}

export interface WorkspaceRecommendation {
  id: number; work_type?: WorkType; workplace?: WorkplaceRef;
  text: string; priority: string; status: string;
  creator: UserRef; created_at: string;
}

const BASE = '/work-optimization';

// Work Types
export const getWorkTypes = () => api.get<WorkType[]>(`${BASE}/work-types`).then(r => r.data);
export const createWorkType = (name: string) => api.post<WorkType>(`${BASE}/work-types`, { name }).then(r => r.data);
export const updateWorkType = (id: number, name: string) => api.put<WorkType>(`${BASE}/work-types/${id}`, { name }).then(r => r.data);
export const deleteWorkType = (id: number) => api.delete(`${BASE}/work-types/${id}`);

// Operations
export const getOperations = (work_type_id?: number) =>
  api.get<Operation[]>(`${BASE}/operations`, { params: work_type_id ? { work_type_id } : {} }).then(r => r.data);
export const createOperation = (data: Omit<Operation, 'id' | 'work_type'>) =>
  api.post<Operation>(`${BASE}/operations`, data).then(r => r.data);
export const updateOperation = (id: number, data: Partial<Omit<Operation, 'id' | 'work_type'>>) =>
  api.put<Operation>(`${BASE}/operations/${id}`, data).then(r => r.data);
export const deleteOperation = (id: number) => api.delete(`${BASE}/operations/${id}`);

// Sessions
export const getSessions = () => api.get<TimingSessionListItem[]>(`${BASE}/sessions`).then(r => r.data);
export const createSession = (data: { work_type_id: number; workplace_id?: number; individual_id?: number; car_info?: string; notes?: string }) =>
  api.post<TimingSession>(`${BASE}/sessions`, data).then(r => r.data);
export const getSession = (id: number) => api.get<TimingSession>(`${BASE}/sessions/${id}`).then(r => r.data);
export const completeSession = (id: number) => api.post<TimingSession>(`${BASE}/sessions/${id}/complete`).then(r => r.data);
export const deleteSession = (id: number) => api.delete(`${BASE}/sessions/${id}`);

// Events
export const addEvent = (sessionId: number, data: { operation_id?: number; custom_name?: string; category: OperationCategory; timestamp: string }) =>
  api.post<TimingEvent>(`${BASE}/sessions/${sessionId}/events`, data).then(r => r.data);

// Analysis
export const getAnalysis = (sessionId: number) =>
  api.get<SessionAnalysis>(`${BASE}/sessions/${sessionId}/analysis`).then(r => r.data);

// Procedures
export const getProcedures = () => api.get<OptimalProcedure[]>(`${BASE}/procedures`).then(r => r.data);
export const createProcedure = (data: { work_type_id: number; workplace_id?: number; name: string; steps: ProcedureStep[] }) =>
  api.post<OptimalProcedure>(`${BASE}/procedures`, data).then(r => r.data);
export const updateProcedure = (id: number, data: { name?: string; steps?: ProcedureStep[] }) =>
  api.put<OptimalProcedure>(`${BASE}/procedures/${id}`, data).then(r => r.data);
export const deleteProcedure = (id: number) => api.delete(`${BASE}/procedures/${id}`);

// Recommendations
export const getRecommendations = (params?: { work_type_id?: number; workplace_id?: number }) =>
  api.get<WorkspaceRecommendation[]>(`${BASE}/recommendations`, { params }).then(r => r.data);
export const createRecommendation = (data: { work_type_id?: number; workplace_id?: number; text: string; priority?: string }) =>
  api.post<WorkspaceRecommendation>(`${BASE}/recommendations`, data).then(r => r.data);
export const updateRecommendation = (id: number, data: { text?: string; priority?: string; status?: string }) =>
  api.put<WorkspaceRecommendation>(`${BASE}/recommendations/${id}`, data).then(r => r.data);
export const deleteRecommendation = (id: number) => api.delete(`${BASE}/recommendations/${id}`);
